import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { SlidersHorizontal, X } from "lucide-react";

const CATEGORIES = [
    { slug: "all", label: "All" },
    { slug: "tops", label: "Tops" },
    { slug: "bottoms", label: "Bottoms" },
];

const SIZES = ["XS", "S", "M", "L", "XL", "28", "30", "32", "34", "36", "7", "8", "9", "10", "11", "12", "OS"];

const Shop = () => {
    const { category } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCat, setActiveCat] = useState(category || "all");
    const [size, setSize] = useState("");
    const [sort, setSort] = useState("");
    const [search, setSearch] = useState(() => searchParams.get("search") || "");
    const [filtersOpen, setFiltersOpen] = useState(false);
    const searchInputRef = useRef(null);

    useEffect(() => {
        setActiveCat(category || "all");
    }, [category]);

    useEffect(() => {
        const urlSearch = searchParams.get("search") || "";
        if (urlSearch !== search) {
            setSearch(urlSearch);
        }
    }, [searchParams, search]);

    useEffect(() => {
        const nextParams = new URLSearchParams(searchParams);

        if (search) {
            nextParams.set("search", search);
        } else {
            nextParams.delete("search");
        }

        if (nextParams.toString() !== searchParams.toString()) {
            setSearchParams(nextParams, { replace: true });
        }
    }, [search, searchParams, setSearchParams]);

    useEffect(() => {
        if (searchParams.get("focusSearch") !== "1") {
            return;
        }

        searchInputRef.current?.focus();
        searchInputRef.current?.select();

        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("focusSearch");
        setSearchParams(nextParams, { replace: true });
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        setLoading(true);
        fetchProducts({
            category: activeCat,
            size: size || undefined,
            sort: sort || undefined,
            search: search || undefined,
        })
            .then((data) => {
                if (activeCat === "all") {
                    setProducts(data.filter((product) => !product.is_voucher));
                    return;
                }
                setProducts(data);
            })
            .finally(() => setLoading(false));
    }, [activeCat, size, sort, search]);

    const colorPool = useMemo(() => {
        const s = new Set();
        products.forEach((p) => p.colors?.forEach((c) => s.add(c)));
        return [...s];
    }, [products]);

    return (
        <div data-testid="shop-page">
            {/* Header */}
            <section className="border-b-4 border-ink px-4 md:px-8 py-10 md:py-16">
                <span className="font-mono-ui text-xs uppercase tracking-[0.3em] text-hazard">
                    [Catalogue / FW26]
                </span>
                <h1 className="font-heading text-6xl md:text-8xl lg:text-9xl uppercase leading-[0.85] mt-3">
                    {CATEGORIES.find((c) => c.slug === activeCat)?.label || "All"}
                </h1>
                <p className="mt-4 font-mono-ui text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    {products.length} pieces / once they&apos;re gone, they&apos;re gone.
                </p>
                <p className="mt-2 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-hazard">
                    All category results here are sourced from Handknitted images.
                </p>
            </section>

            {/* Toolbar */}
            <section className="sticky top-16 z-30 border-b-2 border-ink bg-background md:top-20">
                <div className="px-4 md:px-8 py-3 flex flex-wrap items-center gap-2 md:gap-4">
                    <button
                        type="button"
                        onClick={() => setFiltersOpen((o) => !o)}
                        className="md:hidden inline-flex items-center gap-2 border-2 border-ink px-3 py-2 font-mono-ui text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-black"
                        data-testid="filters-toggle"
                    >
                        <SlidersHorizontal className="w-4 h-4" />
                        Filters
                    </button>

                    <div className="hidden md:flex items-center gap-1">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.slug}
                                type="button"
                                onClick={() => setActiveCat(c.slug)}
                                className={`px-3 py-2 font-mono-ui text-[11px] uppercase tracking-[0.2em] border-2 ${
                                    activeCat === c.slug
                                        ? "bg-ink text-black border-ink"
                                        : "border-transparent hover:border-ink"
                                }`}
                                data-testid={`category-tab-${c.slug}`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>

                    <div className="ml-auto flex items-center gap-2">
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-32 border-2 border-black bg-white px-3 py-2 font-mono-ui text-[11px] uppercase tracking-[0.15em] text-black placeholder:text-black/60 focus:border-hazard focus:outline-none md:w-48"
                            data-testid="search-input"
                        />
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            className="border-2 border-black bg-white px-3 py-2 font-mono-ui text-[11px] uppercase tracking-[0.15em] text-black focus:border-hazard focus:outline-none"
                            data-testid="sort-select"
                        >
                            <option value="">Sort: Featured</option>
                            <option value="price_asc">Price ↑</option>
                            <option value="price_desc">Price ↓</option>
                        </select>
                    </div>
                </div>

                {filtersOpen && (
                    <div className="md:hidden border-t-2 border-ink p-4 grid grid-cols-3 gap-2">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.slug}
                                type="button"
                                onClick={() => {
                                    setActiveCat(c.slug);
                                    setFiltersOpen(false);
                                }}
                                className={`px-2 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] border-2 ${
                                    activeCat === c.slug
                                        ? "bg-ink text-black border-ink"
                                        : "border-ink"
                                }`}
                                data-testid={`mobile-category-tab-${c.slug}`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                )}
            </section>

            {/* Active filter chips */}
            {(size || sort || search) && (
                <div className="px-4 md:px-8 py-3 flex flex-wrap items-center gap-2 border-b-2 border-dotted border-ink/45">
                    <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        Active:
                    </span>
                    {size && (
                        <Chip onClear={() => setSize("")}>Size {size}</Chip>
                    )}
                    {search && (
                        <Chip onClear={() => setSearch("")}>“{search}”</Chip>
                    )}
                    {sort && (
                        <Chip onClear={() => setSort("")}>
                            {sort.replace("_", " ")}
                        </Chip>
                    )}
                </div>
            )}

            {/* Grid */}
            <section className="px-0 md:px-0 border-b-4 border-ink">
                {loading ? (
                    <div className="p-20 text-center font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                        Loading drop…
                    </div>
                ) : products.length === 0 ? (
                    <div className="p-20 text-center" data-testid="no-products">
                        <p className="font-heading text-4xl uppercase">No matches.</p>
                        <p className="mt-2 font-mono-ui text-xs uppercase tracking-[0.2em] text-muted-foreground">
                            Try clearing filters.
                        </p>
                    </div>
                ) : (
                    <div
                        className="grid grid-cols-2 lg:grid-cols-4"
                        data-testid="products-grid"
                    >
                        {products.map((p, idx) => (
                            <div
                                key={p.id}
                                className={`${idx % 2 === 1 ? "border-l-2" : ""} ${
                                    idx % 4 !== 3 ? "lg:border-r-2" : ""
                                } border-t-2 border-black`}
                            >
                                <ProductCard product={p} />
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Size quick filter */}
            <section className="border-b-4 border-ink bg-white px-4 py-10 md:px-8">
                <h3 className="font-mono-ui text-xs uppercase tracking-[0.25em] mb-4">
                    // Filter By Size
                </h3>
                <div className="flex flex-wrap gap-2">
                    {SIZES.map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setSize(size === s ? "" : s)}
                            className={`min-w-[3rem] border-2 border-black bg-white px-3 py-2 font-mono-ui text-xs ${
                                size === s
                                    ? "text-hazard"
                                    : "text-black hover:border-hazard hover:text-hazard"
                            }`}
                            data-testid={`size-filter-${s}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
                {colorPool.length > 0 && (
                    <>
                        <h3 className="font-mono-ui text-xs uppercase tracking-[0.25em] mb-4 mt-8">
                            // Available Colors
                        </h3>
                        <div className="flex flex-wrap gap-2">
                            {colorPool.map((c) => (
                                <span
                                    key={c}
                                    className="border-2 border-black bg-white px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-black"
                                >
                                    {c}
                                </span>
                            ))}
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};

const Chip = ({ children, onClear }) => (
    <span className="inline-flex items-center gap-2 border-2 border-ink px-2 py-1 font-mono-ui text-[10px] uppercase tracking-[0.2em]">
        {children}
        <button type="button" onClick={onClear} aria-label="Clear">
            <X className="w-3 h-3" />
        </button>
    </span>
);

export default Shop;
